import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  Button,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const IMAGE_BASE_URL_LARGE = 'https://media.server.theshootingwarehouse.com/large/';
const IMAGE_BASE_URL_HIRES = 'https://media.server.theshootingwarehouse.com/hires/';
const labelSx = { fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 };
const valueSx = { fontWeight: 600, color: 'text.primary' };

const SpecRow = ({ label, value }) => (
  <Grid container spacing={1} sx={{ py: 0.5 }}>
    <Grid item xs={4} sm={3}>
      <Typography variant='body2' sx={labelSx}>
        {label}
      </Typography>
    </Grid>
    <Grid item xs={8} sm={9}>
      <Typography variant='body2' sx={valueSx}>
        {value || '\u2014'}
      </Typography>
    </Grid>
  </Grid>
);

const NotesLines = ({ lines = 5 }) => (
  <Stack spacing={1.2} sx={{ mt: 1 }}>
    {Array.from({ length: lines }).map((_, idx) => (
      <Box key={idx} sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 18 }} />
    ))}
  </Stack>
);

const ItemSpecSheet = () => {
  const { itemNumber } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [hasImage, setHasImage] = useState(true);
  const [imageSrc, setImageSrc] = useState('');
  const [imageReady, setImageReady] = useState(true);
  const [hiddenImageReady, setHiddenImageReady] = useState(true);
  const [triedHires, setTriedHires] = useState(false);
  const contentRef = useRef(null);

  const printedOn = useMemo(
    () =>
      new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    []
  );

  const facets = useMemo(() => {
    if (!item?.facets) return [];
    try {
      const parsed = JSON.parse(item.facets);
      return Object.entries(parsed).map(([key, val]) => ({
        key,
        value: val,
      }));
    } catch (e) {
      return [];
    }
  }, [item?.facets]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setNotFound(false);
    setHasImage(true);
    setImageSrc('');
    setImageReady(false);
    setHiddenImageReady(false);
    setPdfGenerated(false);
    setGeneratingPdf(false);
    setTriedHires(false);

    axios
      .get(`/itemSpecSheet/${itemNumber}`)
      .then(({ data }) => {
        if (!active) return;
        const record = Array.isArray(data) ? data[0] : data;
        const hasContent = record && Object.keys(record).length > 0;
        setItem(hasContent ? record : null);
        setNotFound(!hasContent);
      })
      .catch(() => {
        if (!active) return;
        setError('Unable to load item spec sheet.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [itemNumber]);

  const headline = item?.description || 'Item Specification Sheet';

  useEffect(() => {
    if (item?.itemno) {
      setHasImage(true);
      setImageReady(false);
      setHiddenImageReady(false);
      setTriedHires(false);
      setImageSrc(`${IMAGE_BASE_URL_LARGE}${item.itemno}.jpg`);
    } else {
      setHasImage(false);
      setImageReady(true);
      setHiddenImageReady(true);
      setImageSrc('');
    }
  }, [item?.itemno]);

  const handleImageLoad = () => {
    setHasImage(true);
    setImageReady(true);
  };

  const handleImageError = () => {
    if (!triedHires && item?.itemno) {
      setTriedHires(true);
      setImageSrc(`${IMAGE_BASE_URL_HIRES}${item.itemno}.png`);
    } else {
      setHasImage(false);
      setImageReady(true);
      setHiddenImageReady(true);
      setImageSrc('');
    }
  };

  const generatePdf = async () => {
    if (!item || !contentRef.current) return;
    if (imageSrc && (!imageReady || !hiddenImageReady)) return;

    setGeneratingPdf(true);
    // Ensure the image has been painted before capture
    if (imageSrc) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    const canvas = await html2canvas(contentRef.current, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
    });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'pt', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgProps = pdf.getImageProperties(imgData);
    let pdfWidth = pageWidth;
    let pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    const topMargin = 20;
    const availableHeight = pageHeight - topMargin;

    if (pdfHeight > availableHeight) {
      const scale = availableHeight / pdfHeight;
      pdfHeight = pageHeight;
      pdfWidth = pdfWidth * scale;
    }

    const marginX = (pageWidth - pdfWidth) / 2;
    const marginY = topMargin;

    pdf.addImage(imgData, 'PNG', marginX, marginY, pdfWidth, pdfHeight, undefined, 'FAST');

    pdf.output('dataurlnewwindow');
    setPdfGenerated(true);
    setGeneratingPdf(false);
  };

  useEffect(() => {
    if (!loading && item && imageReady && hiddenImageReady && !pdfGenerated && !generatingPdf) {
      generatePdf();
    }
  }, [loading, item, imageReady, hiddenImageReady, pdfGenerated, generatingPdf]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef1f7', py: 4, px: { xs: 1.5, md: 0 } }}>
      <Box sx={{ maxWidth: 720, mx: 'auto', textAlign: 'center' }}>
        {loading && (
          <Stack alignItems='center' spacing={2} sx={{ py: 6 }}>
            <CircularProgress />
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Loading item data...
            </Typography>
          </Stack>
        )}

        {!loading && error && <Alert severity='error'>{error}</Alert>}

        {!loading && notFound && <Alert severity='error'>Invalid Product Number: {itemNumber}</Alert>}

        {!loading && !error && item && (
          <Stack alignItems='center' spacing={2} sx={{ py: 6 }}>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              Generating Spec Sheet...
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              A new tab should open automatically. If not, click below.
            </Typography>
            <Button variant='contained' onClick={generatePdf} disabled={generatingPdf}>
              Open Spec Sheet
            </Button>
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: '#fff',
                width: '100%',
                maxWidth: 400,
                minHeight: 200,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {imageSrc && hasImage ? (
                <Box
                  component='img'
                  src={imageSrc}
                  alt={headline}
                  sx={{ maxHeight: 200, maxWidth: '100%', objectFit: 'contain' }}
                  crossOrigin='anonymous'
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  {imageReady ? 'Image not available' : 'Loading image...'}
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </Box>

      {/* Hidden render target for html2canvas to capture */}
      <Box
        sx={{
          position: 'absolute',
          left: '-10000px',
          top: 0,
          width: '100%',
          pointerEvents: 'none',
          opacity: 0,
        }}
        aria-hidden
      >
        <Box sx={{ maxWidth: 1040, mx: 'auto' }}>
          <Paper
            elevation={4}
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: 2,
              background: '#fff',
              boxShadow:
                '0 10px 30px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06)',
            }}
            ref={contentRef}
          >
            {!loading && !error && item && (
              <Stack spacing={3}>
                <Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent='space-between'>
                    <Stack spacing={0.5}>
                      <Typography variant='overline' sx={{ color: 'text.secondary' }}>
                        Vendor Name
                      </Typography>
                      <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                        Sports South, LLC
                      </Typography>
                    </Stack>
                    <Stack spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                      <Typography variant='overline' sx={{ color: 'text.secondary' }}>
                        Printed on
                      </Typography>
                      <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                        {printedOn}
                      </Typography>
                    </Stack>
                </Stack>
              </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: '#f8fafc',
                    minHeight: 240,
                  }}
                >
                  {imageSrc && hasImage ? (
                    <Box
                      component='img'
                      src={imageSrc}
                      alt={headline}
                      crossOrigin='anonymous'
                      sx={{ maxHeight: 240, maxWidth: '100%', objectFit: 'contain' }}
                      onLoad={() => setHiddenImageReady(true)}
                      onError={handleImageError}
                    />
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Image not available
                    </Typography>
                  )}
                </Box>

                <Divider />

                <Stack spacing={1}>
                  <Typography variant='overline' sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                    Item Description
                  </Typography>
                  <Typography variant='h6' sx={{ fontWeight: 800, lineHeight: 1.4 }}>
                    {headline}
                  </Typography>
                </Stack>

                <Divider />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <SpecRow label='Item Number' value={item?.itemno} />
                    <SpecRow label='UPC' value={item?.upc} />
                    <SpecRow label='Manufacturer SKU' value={item?.mfgino} />
                    <SpecRow label='Brand' value={item?.brand_name} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SpecRow label='Series' value={item?.series} />
                    <SpecRow label='Model' value={item?.model} />
                    <SpecRow
                      label='Catalog Price'
                      value={item?.catalog ? `$${Number(item.catalog).toFixed(2)}` : ''}
                    />
                    <SpecRow label='Product ID' value={item?.product_id} />
                  </Grid>
                </Grid>

                {!!facets.length && (
                  <Stack spacing={1.5}>
                    <Typography variant='overline' sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                      Specs / Attributes
                    </Typography>
                    <Grid container spacing={2}>
                      {facets.map(({ key, value }) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <SpecRow label={key} value={value} />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                )}

                <Stack spacing={1}>
                  <Typography variant='overline' sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                    Quoted Price
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems='center'>
                    <Box sx={{ flex: 1, borderBottom: '2px solid', borderColor: 'divider', minHeight: 28 }} />
                    <Typography variant='body2' sx={{ color: 'text.secondary', px: 1 }}>
                      Clerk Initials
                    </Typography>
                    <Box sx={{ width: 120, borderBottom: '2px solid', borderColor: 'divider', minHeight: 28 }} />
                  </Stack>
                </Stack>

                <Stack spacing={1}>
                  <Typography variant='overline' sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                    Notes
                  </Typography>
                  <NotesLines lines={6} />
                </Stack>
              </Stack>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ItemSpecSheet;
